import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import fr from "@/content/fr";
import ApplicationForm from "@/components/ApplicationForm";
import { trackViewContent } from "@/lib/analytics";

const fadeUp = {
  hidden: { y: 22, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

/**
 * <ResidencePage /> — shared template for each résidence tab
 * (Streetwear, Beauty…). Content comes entirely from `fr.js`
 * (see `fr.streetwear` / `fr.beauty`), so the two pages stay
 * visually identical and only the copy differs.
 *
 * Props:
 *   - content: the `fr.streetwear` / `fr.beauty` object
 *   - idPrefix: "streetwear" | "beauty" — used for data-testid + analytics
 */
export const ResidencePage = ({ content, idPrefix }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackViewContent({ content_name: `residence_${idPrefix}` });
  }, [idPrefix]);

  return (
    <div data-testid={`${idPrefix}-page`}>
      {/* Banner */}
      <section className="relative pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background: "radial-gradient(120% 80% at 50% 0%, #4A148C 0%, #2A0A52 45%, #15042E 80%)",
          }}
        />
        <div
          aria-hidden
          className="absolute top-10 left-[-10%] w-[520px] h-[520px] rounded-full blur-3xl opacity-25"
          style={{ background: "radial-gradient(closest-side, rgba(233,30,99,0.5), transparent)" }}
        />

        <div className="relative max-w-[900px] mx-auto px-5 sm:px-8 lg:px-12">
          <Link
            to="/"
            data-testid={`${idPrefix}-back`}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-[13px] tracking-[0.1em] uppercase transition"
          >
            <ArrowLeft size={15} />
            {fr.nav.backToHome}
          </Link>

          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            className="mt-8 text-[11px] sm:text-xs tracking-[0.34em] uppercase text-[#E91E63] font-mono"
          >
            {content.eyebrow}
          </motion.p>

          <motion.h1
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            className="mt-4 font-display font-extrabold text-white uppercase leading-[0.96] tracking-[-0.01em]"
            style={{ fontSize: "clamp(2.2rem, 6vw, 4rem)" }}
            data-testid={`${idPrefix}-title`}
          >
            {content.title}
          </motion.h1>

          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            className="mt-4 font-display font-bold text-[#ff8fb1] uppercase leading-[1.15] tracking-[-0.005em] text-lg sm:text-2xl"
          >
            {content.subtitle}
          </motion.p>

          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            className="mt-7 text-[15.5px] sm:text-[17px] leading-[1.8] text-white/72 max-w-[760px]"
          >
            {content.intro}
          </motion.p>
        </div>
      </section>

      {/* Content blocks */}
      <section className="relative py-16 sm:py-20">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-12 space-y-10">
          {content.blocks.map((b) => (
            <motion.div
              key={b.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
              className="rounded-3xl p-6 sm:p-9 bg-white/[0.04] backdrop-blur-xl border border-white/[0.10]"
            >
              <h2 className="font-display font-extrabold text-white uppercase leading-[1.05] tracking-[-0.01em] text-xl sm:text-2xl">
                {b.title}
              </h2>
              <p className="mt-4 text-[15px] leading-[1.8] text-white/72">{b.body}</p>
            </motion.div>
          ))}

          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            className="text-center italic text-white/55 text-[15px] pt-4"
          >
            {content.tagline}
          </motion.p>
        </div>
      </section>

      {/* Contact / application */}
      <section className="relative py-16 sm:py-24" data-testid={`${idPrefix}-contact-section`}>
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{ background: "linear-gradient(180deg, #15042E 0%, #1F0A48 50%, #15042E 100%)" }}
        />
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center max-w-[760px] mx-auto">
            <motion.p
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              className="text-[11px] tracking-[0.34em] uppercase text-[#E91E63] font-mono"
            >
              {content.contact.eyebrow}
            </motion.p>
            <motion.h2
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              className="mt-4 font-display font-extrabold text-white uppercase leading-[0.96] tracking-[-0.01em]"
              style={{ fontSize: "clamp(1.8rem, 4.5vw, 3rem)" }}
            >
              {content.contact.title}
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              className="mt-6 text-[15.5px] sm:text-[16.5px] leading-[1.75] text-white/70"
            >
              {content.contact.body}
            </motion.p>
          </div>

          <ApplicationForm
            idPrefix={idPrefix}
            programLabel={content.contact.programLabel}
            categoryOptions={content.contact.categoryOptions}
          />
        </div>
      </section>
    </div>
  );
};

export default ResidencePage;
